import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSelector } from "react-redux";
import { selectUser } from "../../core/auth/authSlice";
import { useLocalizedFormatters } from "../../shared/hooks/useLocalizedFormatters";
import { User } from "../../shared/models";
import http from "../../shared/utils/http";
import "./Job.scss";

import { useAppDispatch } from "../../app/hooks";
import { addSuccessMessage } from "../../core/errors/errorSlice";
import { JobApiRoutes } from "../../shared/api-routes/job/job-api.routes";
import ClickIconMenu from "../essentials/icon-buttons/click-icon/ClickIconMenu";
import ToggleIcon from "../essentials/icon-buttons/toggle-icon/ToggleIcon";
import ConfirmationModal from "../essentials/modals/confirmation-modal/ConfirmationModal";
import UserCard, { UserPreviewInfo } from "../essentials/user-card/UserCard";
import JobContent from "./content/JobContent";
import EditJob from "./edit-job/EditJob";

export interface JobInfo {
	id: string;
	author: UserPreviewInfo;
	subject: JobSubjects;
	object: string;
	position: string;
	league: { name: string; category: string; id?: string };
	city: string;
	description: string;
	createdAt: string;
	sport: string;
	nationality: string;
	availableToTransfer: boolean;
	cartellinoStatus: string;
	team: string;
	isSaved: boolean;
}

export type JobSubject = "athlete" | "staff" | "team";
export const enum JobSubjects {
	athlete = "athlete",
	staff = "staff",
	team = "team",
}

export type JobProps = {
	jobInfo: JobInfo;
	onDeleteJob?: any;
	onUnsaveJob?: (jobId: string) => void;
	children?: any;
	user?: boolean;
};

const Job: React.FC<JobProps> = ({
	jobInfo,
	children,
	user,
	onDeleteJob,
	onUnsaveJob,
}) => {
	const { actorId } = useSelector(selectUser) as User;
	const { formatRelativeTime } = useLocalizedFormatters();
	const { t } = useTranslation();

	const [saved, setSaved] = useState(jobInfo.isSaved);

	const [userInfo, setUserInfo] = useState<UserPreviewInfo>();

	const toggleSave = () => {
		const saveUrl = JobApiRoutes.SAVE_JOB(jobInfo.id);
		if (saved) {
			http.delete(saveUrl).then((res) => {
				if (res === "savedJob_doc__deleted__success") {
					setSaved(false);
					if (onUnsaveJob) {
						onUnsaveJob(jobInfo.id);
					}
				}
			});
		} else {
			http.post(saveUrl).then((res) => {
				setSaved(true);
			});
		}
	};

	const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>();

	const deleteJob = () => {
		http.delete(JobApiRoutes.DELETE_JOB(jobInfo.id)).then((res) => {
			setDeleteModalOpen(false);
			onDeleteJob(jobInfo.id);
		});
	};

	const appDispatch = useAppDispatch();
	const copyLink = () => {
		navigator.clipboard.writeText(window.location.origin + "/a/" + jobInfo.id);
		appDispatch(
			addSuccessMessage({ title: t("common.messages.copiedToClipboard") }),
		);
	};

	const menuOptions = () => {
		const normalOptions = [
			{ text: t("common.buttons.copyLink"), action: copyLink },
			{ text: t("common.buttons.embed") },
			{ text: t("common.buttons.stopFollowing") },
			{ text: t("common.buttons.report") },
		];

		return actorId === jobInfo.author.id
			? [
					{
						text: t("common.buttons.edit"),
						// action: () => {
						//   setEditModalOpen(true);
						// },
					},
					{
						text: t("common.buttons.delete"),
						color: "red",
						action: () => {
							setDeleteModalOpen(true);
						},
					},
					...normalOptions,
				]
			: normalOptions;
	};

	const [isEditModalOpen, setEditModalOpen] = useState<boolean>(false);

	const onEdit = (newDescription: string) => {
		http
			.patch(JobApiRoutes.MODIFY_JOB(jobInfo.id), {
				description: newDescription,
			})
			.then((res) => {
				jobInfo.position = res.position;
				jobInfo.description = res.description;
				setEditModalOpen(false);
			});
	};

	const [clicked, setClicked] = useState(false);

	return (
		<div
			className={`${
				jobInfo.author.accountType ? "job-" + jobInfo.author.accountType : ""
			} job ${clicked ? "job-clicked" : ""}`}
		>
			<div className="job-header">
				<div className="job-header-left">
					<UserCard
						action={"follow"}
						userInfo={jobInfo.author}
						className="flex-1"
					/>
					<div className="job-date">
						{formatRelativeTime(jobInfo.createdAt)}
					</div>
				</div>
				<ClickIconMenu
					icon="dots"
					tooltip={t("common.tooltips.moreOptions")}
					options={menuOptions()}
				/>
			</div>
			<JobContent
				jobInfo={jobInfo}
				setClicked={setClicked}
			/>
			<div className="save-container">
				<ToggleIcon
					icon="save"
					onClick={() => toggleSave()}
					isToggled={saved}
					fillColor="#00ffff"
					tooltip={t("common.buttons.save")}
				/>
			</div>

			<div>
				<div className="actions">
					<EditJob
						title={t("job.editJob")}
						state={{
							isModalOpen: isEditModalOpen,
							setModalOpen: setEditModalOpen,
						}}
						description={jobInfo.description}
						onEdit={onEdit}
					/>
				</div>
				{isDeleteModalOpen && (
					<ConfirmationModal
						title={t("job.deleteJob")}
						state={{
							isModalOpen: isDeleteModalOpen,
							setModalOpen: setDeleteModalOpen,
						}}
						onConfirm={deleteJob}
					/>
				)}
			</div>
		</div>
	);
};

export default Job;
